import { useEffect, useState, type RefObject } from "react";

/**
 * Returns true while a fixed floating control sits on top of an interactive
 * element (link, button, input), anything marked `data-no-overlap`, or another
 * floating control, so the control can fade away instead of covering page CTAs.
 *
 * `selfId` is this control's `data-floating-control` value. Elements belonging
 * to the same control are ignored; elements belonging to a different control
 * (for example the Ground Me button while the Support button is measuring)
 * count as an obstruction, so two floating buttons can never stack on each
 * other.
 */
export function useAvoidOverlap(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
  selfId?: string,
) {
  const [obstructed, setObstructed] = useState(false);
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const check = () => {
      const node = ref.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      if (!r.width) return;
      const pts: Array<[number, number]> = [
        [r.left + 4, r.top + 4],
        [r.right - 4, r.top + 4],
        [r.left + 4, r.bottom - 4],
        [r.right - 4, r.bottom - 4],
        [(r.left + r.right) / 2, (r.top + r.bottom) / 2],
      ];
      let hit = false;
      for (const [x, y] of pts) {
        for (const el of document.elementsFromPoint(x, y)) {
          if (node.contains(el)) continue;
          const owner = el.closest("[data-floating-control]");
          if (owner) {
            if (!selfId || owner.getAttribute("data-floating-control") === selfId) continue;
            hit = true;
            break;
          }
          if (el.closest("a, button, [role=button], input, textarea, select, [data-no-overlap]")) {
            hit = true;
            break;
          }
        }
        if (hit) break;
      }
      setObstructed(hit);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    const id = window.setInterval(check, 700);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
      window.clearInterval(id);
    };
  }, [ref, enabled]);
  return obstructed;
}
